import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Checkbox, DatePicker, Divider, Form, Input, Modal, Select, Switch } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox/Checkbox';
import InputNumber from 'antd/lib/input-number';
import { WarningFilled } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { RangeValue } from 'rc-picker/lib/interface';
import { appConfig } from '../../../settings';
import {
    IBillingSystemCompanySelectItem,
    INonRecurringCharges,
    INonRecurringChargesItem,
    IPostBillingOrder,
    ISelect,
    NotificationType,
} from '../../../interfaces';
import {
    useBillingCompaniesSelect,
    useCompanyInfo,
    useNonRecurringChargesList,
    useRouterStore,
    useUiIsLoading,
} from '../../../hooks';
import {
    addNotification,
    apiHandleErrorMessages,
    apiMessagesGetFormRules,
    apiMessagesGetFormRulesFieldBlank,
    getBillingCompaniesInvoiceNumber,
    getBillingCompaniesSelect,
    getCompanyById,
    getGeneralNonRecurringChargesList,
    getNonRecurringCardsCount,
    getNonRecurringChargesList,
    postBillingSystemOrder,
} from '../../../middleware';
import { disablePrevious6MonthDates, disablePreviousDates, globalHexId } from '../../../instruments';
import { tB, tF } from '../../../translate';
import { CardInfoToolBar, CardLayout, Catcher, FormSelect } from '../../../Components';
import Styles from '../../styles/m_itemCardStyles.less';
import BillingStyle from '../m_styles.less';

const { RangePicker } = DatePicker;
const { Option } = Select;

type totalObject = { [key: string]: number };

interface IFormItem {
    remove: (id: string | number) => Promise<void>;
    id: string;
    formSummaryDescription: (e: any) => any;
    currency: string;
    unit: number;
    amountCents: number;
    setTotal: any;
    isUnitDisabled?: boolean;
}

interface IInitNewItemValue {
    amountCents?: string;
    units?: number;
    description?: string;
}

interface IModuleContent {
    nrcList: INonRecurringCharges;
    addNewItem: (
        id?: number | string,
        values?: INonRecurringChargesItem,
        units?: number,
        specialType?: boolean,
    ) => void;
    companyId: string;
}

const ModuleContent: React.FC<IModuleContent> = observer(({ nrcList, addNewItem, companyId }: IModuleContent) => {
    const [foundCards, setFoundCards] = React.useState<number>();
    const onClick = (id?: number | string, values?: INonRecurringChargesItem, unit = 1, specialType = false) => {
        addNewItem(id, values, unit, specialType);
    };

    const onDateChoose = async (_: RangeValue<Dayjs>, dates: Array<string>) => {
        if (!dates[0] || !dates[1]) return;

        const count = await getNonRecurringCardsCount(companyId, { startDate: dates[0], endDate: dates[1] });

        setFoundCards(count);
    };

    return (
        <div style={{ padding: '10px' }}>
            <div className={Styles.mrcFormItem}>
                <h3>Nrc available for this company:</h3>
                <div
                    style={{
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '20px',
                    }}
                >
                    {nrcList.map((value) => {
                        return (
                            <>
                                <div className={Styles.mrcFormItemContainer} key={value.id} style={{ width: '250px' }}>
                                    {value.predefinedType ? (
                                        <div
                                            className={Styles.mrcFormItemContainerHeader}
                                            style={{ marginBottom: '5px' }}
                                        >
                                            {value.predefinedType === 'cards' ? 'Type: Monthly Sims' : ''}
                                        </div>
                                    ) : null}
                                    <span>{value.name}</span>
                                    <div style={{ color: 'green' }}>EUR {value.amountCents}</div>
                                    {value.predefinedType ? (
                                        <>
                                            <RangePicker
                                                disabledDate={disablePrevious6MonthDates}
                                                onChange={onDateChoose}
                                            />
                                            {typeof foundCards === 'number' && foundCards > 0 ? (
                                                <>
                                                    <Divider />
                                                    <div>Cards found for this data period - {foundCards}</div>
                                                    <Button
                                                        onClick={() => onClick(value.id, value, foundCards, true)}
                                                        type={'primary'}
                                                    >
                                                        Add
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <div>No cards found for this data period!</div>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <Button onClick={() => onClick(value.id, value)} type={'primary'}>
                                            Add
                                        </Button>
                                    )}
                                </div>
                            </>
                        );
                    })}
                </div>
            </div>
            <Divider />
            <div
                style={{
                    justifyContent: 'center',
                    display: 'flex',
                }}
            >
                <div
                    style={{
                        border: '1px dashed white',
                        width: '250px',
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <h4 style={{ fontSize: 16 }}>New product</h4>
                    <Button key="submit" onClick={() => onClick()} style={{ width: '100%' }}>
                        {tB('Add')}
                    </Button>
                </div>
            </div>
        </div>
    );
});

const FormItem: React.FC<IFormItem> = observer(
    ({
        remove,
        id,
        formSummaryDescription,
        currency,
        unit = 0,
        amountCents = 0,
        setTotal,
        isUnitDisabled = false,
    }: IFormItem) => {
        const [units, setUnits] = useState(unit);
        const [amount, setAmount] = useState(amountCents);

        const onUnitChange = (value: any) => {
            setUnits(value);
        };

        const onAmountChange = (value: any) => {
            setAmount(value);
        };

        useEffect(() => {
            setTotal((prevState: totalObject) => {
                return {
                    ...prevState,
                    [id]: amount * units,
                };
            });
        }, [units, amount]);

        const total = units * amount;

        return (
            <div className={BillingStyle.newOrderListItem}>
                {isUnitDisabled ? (
                    <div className={BillingStyle.newOrderPredefinedBanner}>
                        <WarningFilled /> This NRC is of Predefined Type, unit count cannot be edited.
                    </div>
                ) : null}
                <Form.Item name={'description-' + id} label={'Description'} rules={apiMessagesGetFormRulesFieldBlank()}>
                    <Input
                        className={BillingStyle.newOrderListInputs}
                        onChange={(e) => formSummaryDescription(e.target.value)}
                    />
                </Form.Item>
                <div className={BillingStyle.newOrderListItemUnits}>
                    <Form.Item
                        required={true}
                        name={'units-' + id}
                        label={'Units'}
                        rules={apiMessagesGetFormRulesFieldBlank()}
                        style={{ width: '100%' }}
                    >
                        <InputNumber
                            step={1}
                            disabled={isUnitDisabled}
                            min={1}
                            className={
                                isUnitDisabled
                                    ? BillingStyle.newOrderListInputsDisabled
                                    : BillingStyle.newOrderListInputs
                            }
                            onChange={onUnitChange}
                        />
                    </Form.Item>
                    <Form.Item
                        required={true}
                        name={'amountCents-' + id}
                        label={'Amount'}
                        rules={apiMessagesGetFormRulesFieldBlank()}
                        style={{ width: '100%' }}
                    >
                        <InputNumber
                            step={1}
                            min={0.01}
                            precision={2}
                            className={BillingStyle.newOrderListInputs}
                            onChange={onAmountChange}
                        />
                    </Form.Item>
                </div>
                <div className={BillingStyle.newOrderListItemTotal}>
                    <Button danger onClick={() => remove(id)}>
                        Delete
                    </Button>
                    <div className={BillingStyle.total}>
                        Price - {currency || 'EUR'} {total.toFixed(2)}
                    </div>
                </div>
            </div>
        );
    },
);

export const BillingNewOrder: React.FC = observer(() => {
    const [currency, setCurrency] = useState<string>('EUR');
    const [invoicingCompanyId, setInvoicingCompanyId] = useState<string>('');
    const [companyId, setCompanyId] = useState<string>('');
    const companyInfo = useCompanyInfo();
    const isPostPaid = companyInfo.paymentTypeName === 'Post-Paid';
    const routerStore = useRouterStore();
    const billingCompaniesSelect = useBillingCompaniesSelect();
    const isLoading = useUiIsLoading();
    const [form] = Form.useForm();
    const [newFormArray, setNewFormArray] = useState<any>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDescriptionEdited, setDescriptionEdited] = useState(false);
    const [summaryDescription, setSummaryDescription] = useState('');
    const [isDiscount, setIsDiscount] = useState(false);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [discountUnit, setDiscountUnit] = useState('');
    const [isDeducedChecked, setIsDeducedChecked] = useState(true);
    const [total, setTotal] = useState<{ [key: string]: number }>({});
    const nrcList = useNonRecurringChargesList();
    const [generalNrcList, setGeneralNrcList] = useState<INonRecurringCharges>([]);
    const [invoiceNumber, setInvoiceNumber] = useState<string>('');
    const [invoiceDate, setInvoiceDate] = useState<dayjs.Dayjs | null>(null);
    const [sendInvoice, setSendInvoice] = useState<boolean>();
    const [sendInvoiceOnDate, setSendInvoiceOnDate] = useState<boolean>();
    const [overduePeriod, setOverduePeriod] = useState<number>(0);

    const paymentMethods = appConfig.paymentMethods;

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleOpen = () => {
        setIsModalOpen(true);
    };

    const getCompaniesSelectOptions = async () => {
        await getBillingCompaniesSelect({ isInvoicing: false, isInvoiceable: true });
    };

    const fetchNrcList = async () => {
        await getCompanyById(companyId);
        await getNonRecurringChargesList(companyId);

        const generalNrcs = await getGeneralNonRecurringChargesList();
        setGeneralNrcList(generalNrcs);
    };

    useEffect(() => {
        getCompaniesSelectOptions();
    }, []);

    useEffect(() => {
        if (companyId) {
            fetchNrcList();
        }
    }, [companyId]);

    const extractItems = (input: { [key: string]: string | number | undefined }) => {
        const itemProps: {
            [id: string]: {
                amountCents?: string;
                description?: string;
                units?: number;
                [key: string]: any;
            };
        } = {};
        const ids = new Set<string>();

        const idPattern = /-new:(\d+)$/;

        for (const key in input) {
            const match = key.match(idPattern);
            if (match) {
                const id = match[1];
                ids.add(id);

                const propName = key.split('-new:')[0];
                if (!itemProps[id]) itemProps[id] = {};
                itemProps[id][propName] = input[key];
            }
        }

        return Array.from(ids).map((id) => itemProps[id]);
    };

    const countTotal = () => {
        let totalValue = Object.values(total).reduce((reducer, value) => reducer + value, 0);

        if (isDiscount && discountUnit === 'percentage') {
            const value = (discountAmount / 100) * totalValue;

            totalValue = value >= totalValue ? 0 : totalValue - value;
        } else if (isDiscount) {
            totalValue = discountAmount >= totalValue ? 0 : totalValue - discountAmount;
        }

        return totalValue;
    };

    useEffect(() => {
        if (!isDeducedChecked) {
            form.setFieldsValue({
                dueDate: dayjs().add(overduePeriod, 'day'),
            });
        } else {
            form.setFieldsValue({ dueDate: undefined });
        }
    }, [isDeducedChecked]);

    const onSubmit = async () => {
        try {
            await form.validateFields();
            const values = await form.getFieldsValue();
            const productList = extractItems(values);
            const totalValue = countTotal();
            const order: IPostBillingOrder = {
                amount: totalValue.toString(),
                companyId: +companyId,
                deductFromBalance: isPostPaid ? false : isDeducedChecked,
                description: values.summaryDescription,
                discount: {
                    description: values.discountDescription,
                    amount: values.discountAmountCents,
                    amountType: values.discountUnits,
                },
                invoiceDate: values.invoiceDate,
                dueDate: values.dueDate,
                paymentType: paymentMethods.crediting,
                products: productList.map((value) => ({
                    unitPrice: value.amountCents,
                    units: value.units,
                    description: value.description,
                })),
                sendInvoice: isPostPaid ? false : Boolean(values.sendInvoice),
                sendInvoiceOnDate: isPostPaid ? false : Boolean(values.sendInvoiceOnDate),
                statusManual: isPostPaid ? 'DRAFT' : undefined,
                purchaseOrderNumber: values.purchaseOrderNumber,
            };

            const resp = await postBillingSystemOrder(order);

            if (resp.isError) {
                apiHandleErrorMessages(resp || {});

                return;
            }

            addNotification({
                type: NotificationType.SUCCESS,
                message: 'The order has successfully been created.',
                duration: appConfig.notificationDuration,
            });

            await routerStore.goTo('BillingOrdersList');
        } catch (e) {
            console.log(e);
        }
    };

    const formSummaryDescription = (value: any, isNew?: boolean) => {
        if (isDescriptionEdited) return;

        const totalFormLength = isNew ? newFormArray.length + 1 : newFormArray.length;

        let generatedDescription = '';

        if (totalFormLength <= 1) {
            generatedDescription = value;
        }

        if (totalFormLength >= 2) {
            generatedDescription = 'Several Items';
        }

        form.setFieldValue('summaryDescription', generatedDescription);

        setSummaryDescription(generatedDescription);
    };

    const addNewItem = (
        nrcId?: string | number,
        nrcValues?: INonRecurringChargesItem,
        units?: number,
        specialType?: boolean,
    ) => {
        const id = globalHexId.getId();

        const initValues = {
            description: nrcValues?.name || 'New Product',
            amountCents: nrcValues?.amountCents || '1',
            units: units || 1,
        } as IInitNewItemValue;

        const initNewItemValue = {
            specialType,
            amountCents: 1,
            id,
            units: units || 1,
            description: 'New Product',
            ...initValues,
            nrcId,
        };

        form.setFieldValue('units-' + id, units || 1);
        form.setFieldValue('amountCents-' + id, initNewItemValue.amountCents);
        form.setFieldValue('description-' + id, initNewItemValue.description);
        formSummaryDescription(initNewItemValue.description, true);

        setNewFormArray([...newFormArray, initNewItemValue]);
        handleCancel();
    };

    const removeItem = async (id: string | number) => {
        const filteredArr = newFormArray.filter((value: any) => value.id !== id);

        delete total[id];

        setNewFormArray([...filteredArr]);
        setTotal(total);
    };

    const onCompanyChange = (value: any) => {
        const company = billingCompaniesSelect.find(
            (company) => company.id === value,
        ) as IBillingSystemCompanySelectItem;

        const filteredFormArry = newFormArray?.filter((value: any) => !value?.nrcId);
        const nrcIds = newFormArray?.filter((value: any) => value?.nrcId).map((i: any) => i.id);

        nrcIds.forEach((id: string) => {
            delete total[id];
        });

        setTotal(total);
        setCurrency(company.currencyAbbreviation);
        setCompanyId(company.id);
        setOverduePeriod(company.paymentTerms || 0);
        setInvoicingCompanyId(company.invoicedByCompanyId);
        setNewFormArray(filteredFormArry);
        setDiscountUnit('');
        setDiscountAmount(0);
        form.setFieldValue('discountUnits', '');
        form.setFieldValue('discountAmountCents', '');

        if (!company.invoicedByCompanyId && !isDeducedChecked) {
            onDeductFromBalanceCheck();
        }
    };

    const descriptionOnChange = (e: any) => {
        setDescriptionEdited(true);

        form.setFieldValue('summaryDescription', e.target.value);

        setSummaryDescription(e.target.value);
    };

    const discountOnDelete = () => {
        setIsDiscount(false);
        setDiscountAmount(0);
        setDiscountUnit('');
        form.setFieldValue('discountDescription', '');
        form.setFieldValue('discountUnits', '');
        form.setFieldValue('discountAmountCents', '');
    };

    const onDeductFromBalanceCheck = () => {
        form.setFieldValue('invoiceDate', '');
        setIsDeducedChecked(!isDeducedChecked);
        setInvoiceNumber('');
    };

    const selectDiscountListJSX = [
        { id: 'percentage', name: '%' },
        { id: 'currency', name: currency },
    ].map(
        (s: ISelect): JSX.Element => (
            <Option key={s.id} value={s.id}>
                <div>{s.name}</div>
            </Option>
        ),
    );

    const onChangeDate = async (date: dayjs.Dayjs | null) => {
        setInvoiceDate(date);
        if (date) {
            const formatedDate = date.format('YYYY-MM-DD');

            const { isError, resp } = await getBillingCompaniesInvoiceNumber({ formatedDate, companyId });

            if (isError) {
                addNotification({ type: NotificationType.ERROR, message: 'Error fetching invoice number' });
            }

            const parsedResp = resp as { invoice_number: string };

            if (parsedResp.invoice_number) {
                setInvoiceNumber(parsedResp.invoice_number);
            }
            form.setFieldValue('invoiceDate', date);
            form.setFieldValue('dueDate', date.add(overduePeriod, 'day'));
        } else {
            setInvoiceNumber('');
            form.setFieldValue('invoiceDate', undefined);
        }
    };

    const onChangeSendInvoice = (e: CheckboxChangeEvent) => {
        setSendInvoice(e.target.checked);
        form.setFieldValue('sendInvoice', e.target.checked);
        if (!e.target.checked) {
            setSendInvoiceOnDate(false);
            form.setFieldValue('sendInvoiceOnDate', false);
        }
    };
    const onChangeSendInvoiceOnDate = (checked: boolean) => {
        setSendInvoiceOnDate(checked);
        form.setFieldValue('sendInvoiceOnDate', checked);
    };

    const nrcIds = newFormArray?.filter((value: any) => value?.nrcId).map((value: any) => value?.nrcId);
    const filteredCompanyNrc = nrcList.filter((value: any) => !nrcIds.includes(value.id));
    const filteredGeneralNrc = generalNrcList.filter((value: any) => !nrcIds.includes(value.id));
    const filteredNrc = [...filteredCompanyNrc, ...filteredGeneralNrc];
    const totalValue = countTotal();

    return (
        <Catcher>
            <article className={Styles.main}>
                <CardLayout idSpinners={['GET_COMPANIES_SELECT', 'POST_BILLING_SYSTEM_ORDER']} titleLeft={`New Order`}>
                    <Form form={form} name="newBillingOrder" layout={'vertical'} className={'formStyle'}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <div style={{ width: '100%' }}>
                                <FormSelect
                                    required={true}
                                    isNotClear={true}
                                    fieldName={'companyId'}
                                    label={tF('Company')}
                                    useSelectHook={useBillingCompaniesSelect}
                                    rules={apiMessagesGetFormRulesFieldBlank()}
                                    onChange={onCompanyChange}
                                />
                            </div>
                            <Button
                                style={{ marginTop: '5px' }}
                                type={'primary'}
                                onClick={() => {
                                    routerStore.goTo('BillingCompanyNew');
                                }}
                            >
                                + New
                            </Button>
                        </div>
                        <div className={BillingStyle.newOrderListForm}>
                            {newFormArray.map((field: any) => {
                                return (
                                    <div key={field.id}>
                                        <FormItem
                                            isUnitDisabled={field.specialType}
                                            unit={field.units}
                                            amountCents={field.amountCents}
                                            currency={currency}
                                            remove={removeItem}
                                            id={field.id}
                                            formSummaryDescription={formSummaryDescription}
                                            setTotal={setTotal}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <Button
                            type="dashed"
                            onClick={filteredNrc.length ? handleOpen : () => addNewItem()}
                            className={Styles.mrcAddButton}
                            disabled={!companyId}
                        >
                            Add Product
                        </Button>
                        <Divider />
                        <div className={BillingStyle.newOrderSummary}>
                            <div className={BillingStyle.newOrderSummaryTitle}>Summary: </div>
                            <div>
                                <Form.Item name={'summaryDescription'} rules={apiMessagesGetFormRulesFieldBlank()}>
                                    <Input
                                        placeholder={'Summary'}
                                        onChange={(e) => descriptionOnChange(e)}
                                        value={summaryDescription}
                                    />
                                </Form.Item>
                            </div>
                            <div className={BillingStyle.newOrderSummaryDiscountContainer}>
                                {!isDiscount ? (
                                    <Button
                                        type={'primary'}
                                        onClick={() => setIsDiscount(true)}
                                        disabled={newFormArray.length === 0 || !companyId}
                                    >
                                        + Add Discount
                                    </Button>
                                ) : (
                                    <div className={BillingStyle.newOrderSummaryDiscountItem}>
                                        <Form.Item
                                            name={'discountDescription'}
                                            label={'Discount description'}
                                            rules={apiMessagesGetFormRulesFieldBlank()}
                                            style={{ flex: 3 }}
                                        >
                                            <Input className={BillingStyle.newOrderSummaryDiscountInputs} />
                                        </Form.Item>
                                        <Form.Item
                                            name={'discountUnits'}
                                            label={'Units'}
                                            rules={apiMessagesGetFormRulesFieldBlank()}
                                            style={{ flex: 1 }}
                                        >
                                            <Select
                                                className={BillingStyle.newOrderSummaryDiscountInputs}
                                                allowClear={false}
                                                onChange={(value) => setDiscountUnit(value)}
                                                placeholder={'Unit'}
                                            >
                                                {selectDiscountListJSX}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            required={true}
                                            name={'discountAmountCents'}
                                            label={'Amount'}
                                            rules={apiMessagesGetFormRulesFieldBlank()}
                                            style={{ flex: 1 }}
                                        >
                                            <InputNumber
                                                step={1}
                                                min={1}
                                                className={BillingStyle.newOrderSummaryDiscountInputs}
                                                onChange={(value) => setDiscountAmount(value || 0)}
                                            />
                                        </Form.Item>
                                        <div className={BillingStyle.newOrderSummaryDiscountItemTotal}>
                                            <Button danger onClick={discountOnDelete} style={{ marginTop: '5px' }}>
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                <div className={BillingStyle.newOrderSummaryDiscountTotal}>
                                    TOTAL: {currency || 'EUR'} {totalValue?.toFixed(2)}
                                </div>
                            </div>
                        </div>
                        <Divider />
                        {!isPostPaid ? (
                            <Form.Item name="deductFromBalance" rules={apiMessagesGetFormRules}>
                                <Checkbox
                                    onChange={onDeductFromBalanceCheck}
                                    checked={isDeducedChecked}
                                    disabled={!invoicingCompanyId}
                                >
                                    Deduct From Balance
                                </Checkbox>
                            </Form.Item>
                        ) : null}
                        {isDeducedChecked && companyId && !isPostPaid ? (
                            <div className={BillingStyle.newOrderSummary}>
                                <div className={BillingStyle.newOrderSummaryTitle}>Company Balance Information:</div>
                                <div style={{ marginLeft: '10px' }}>
                                    <div>
                                        Current Balance: {currency || 'EUR'} {companyInfo.balance}
                                    </div>
                                    <div>
                                        Balance After Deduction: {currency || 'EUR'}{' '}
                                        {(parseFloat(companyInfo.balance || '0') - totalValue).toFixed(2)}
                                    </div>
                                    {parseFloat(companyInfo.balance || '0') - totalValue < 0 && (
                                        <div style={{ color: 'red', fontWeight: 'bold' }}>
                                            Warning: Insufficient balance! Remaining deficit: {currency || 'EUR'}{' '}
                                            {Math.abs(parseFloat(companyInfo.balance || '0') - totalValue).toFixed(2)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                        {!isPostPaid && !isDeducedChecked && companyId ? (
                            <div>
                                <Form.Item
                                    name="invoiceDate"
                                    required
                                    rules={apiMessagesGetFormRules}
                                    label="Invoice Date"
                                >
                                    <div className={BillingStyle.newOrderDateField}>
                                        <DatePicker
                                            onChange={onChangeDate}
                                            allowClear={false}
                                            disabledDate={disablePreviousDates}
                                        />
                                        <div className={BillingStyle.newOrderDeducedInvoiceNumber}>
                                            Possible Invoice Number: {invoiceNumber || 'Choose invoice date'}
                                        </div>
                                    </div>
                                </Form.Item>
                                <Form.Item name="sendInvoice" valuePropName="checked" rules={apiMessagesGetFormRules}>
                                    <Checkbox onChange={onChangeSendInvoice}>Send Invoice</Checkbox>
                                </Form.Item>
                                {Boolean(sendInvoice) && Boolean(invoiceNumber) ? (
                                    <Form.Item
                                        name="sendInvoiceOnDate"
                                        valuePropName="checked"
                                        rules={apiMessagesGetFormRules}
                                    >
                                        <div className={BillingStyle.newOrderDateField}>
                                            <Switch
                                                unCheckedChildren={'Now'}
                                                checkedChildren={'On Date'}
                                                onChange={onChangeSendInvoiceOnDate}
                                            />
                                            {Boolean(sendInvoiceOnDate) && Boolean(invoiceDate) ? (
                                                <div className={BillingStyle.newOrderDeducedInvoiceNumber}>
                                                    {invoiceDate?.format('YYYY-MM-DD')}
                                                </div>
                                            ) : null}
                                        </div>
                                    </Form.Item>
                                ) : null}
                                <Form.Item name="dueDate" required rules={apiMessagesGetFormRules} label="Due date">
                                    <DatePicker allowClear={false} disabledDate={disablePreviousDates} />
                                </Form.Item>
                            </div>
                        ) : null}
                        {companyId && (!isDeducedChecked || isPostPaid) ? (
                            <div>
                                <Form.Item
                                    name={'purchaseOrderNumber'}
                                    label={'PO Number'}
                                    rules={apiMessagesGetFormRules}
                                >
                                    <Input placeholder={'Purchase Order Number'} />
                                </Form.Item>
                            </div>
                        ) : null}
                    </Form>
                    <Divider />
                    <CardInfoToolBar>
                        <Button
                            type={'primary'}
                            disabled={newFormArray.length === 0 || !companyId || isLoading}
                            onClick={onSubmit}
                        >
                            Submit
                        </Button>
                    </CardInfoToolBar>
                </CardLayout>
            </article>
            <Modal
                title="Select product"
                open={isModalOpen}
                wrapClassName={'modal'}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" type="primary" onClick={handleCancel}>
                        {tB('Cancel')}
                    </Button>,
                ]}
                width={700}
            >
                <ModuleContent nrcList={filteredNrc} addNewItem={addNewItem} companyId={companyId} />
            </Modal>
        </Catcher>
    );
});
